<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PoiDetailsSubpoiButton from '@/components/PoiDetailsSubpoiButton.vue';
import { Button, FancyButton, Icon } from '@/components/ui';
import { usePois } from '@/composables';
import { cn } from '@/lib/utils';
import type { Poi } from '@/types';

const props = defineProps<{
  data: Poi;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const { selectedPoi, selectedSubpoiIndex, selectedPoiSubpoisLength } = usePois();

const numberOfSelectedSubpoiLinks = computed(() => props.data.subpois[selectedSubpoiIndex.value]);
</script>

<template>
  <div
    :style="`--filler-50: var(--markets-${data.category})`"
    :class="
      cn(
        'custom-scrollbar relative max-h-full w-full overflow-y-auto overflow-x-hidden rounded-xl bg-white  lg:rounded-bl-[60px] lg:after:w-[calc(100%_-_72px)]',
        $attrs.class ?? '',
      )
    "
  >
    <div class="p-8 pb-0 lg:p-[60px] lg:pb-5">
      <Button
        v-if="selectedSubpoiIndex !== 0"
        @click="selectedSubpoiIndex = 0"
        class="mb-3 gap-2 text-shuttle-gray-500 hover:text-filler-50 focus-visible:text-filler-50"
        variant="ghost"
      >
        <Icon name="chevron-right" class="size-3 -translate-y-px rotate-180" />
        {{ t(`poi-details-card.back`) }}
      </Button>
      <div class="space-y-5">
        <div
          class="flex flex-col-reverse items-end justify-between gap-2 lg:flex-row lg:items-center"
        >
          <div
            class="mr-auto inline-flex h-14 items-center gap-2.5 rounded-full rounded-bl-none bg-filler-50 px-5 py-3 pe-[25px] text-xl font-semibold tracking-wide text-white"
          >
            <Icon class="size-10 shrink-0" :name="data.category" />
            <span class="block capitalize">
              {{ t(`global.categories.${data.category}`) }}
            </span>
          </div>
          <Button
            class="translate-x-4 text-shuttle-gray-500 hover:text-filler-50 focus-visible:text-filler-50"
            variant="ghost"
            @click="emit('close')"
            >{{ t(`poi-details-card.back-to-map`) }}
            <Icon class="size-8" name="close" />
          </Button>
        </div>
        <div class="space-y-7">
          <h2 class="line-clamp-3 text-5xl font-bold leading-normal text-filler-50">
            {{ t(`poi-details-card.${data.category}.${data.name}.${selectedSubpoiIndex}.title`) }}
          </h2>
          <p class="text-xl text-black">
            {{
              t(`poi-details-card.${data.category}.${data.name}.${selectedSubpoiIndex}.description`)
            }}
          </p>
        </div>
        <div v-if="selectedPoi && !selectedSubpoiIndex" class="grid gap-3 pt-8 lg:hidden">
          <PoiDetailsSubpoiButton
            @click="selectedSubpoiIndex = i"
            class="pointer-events-auto px-2 py-1"
            v-for="i in selectedPoiSubpoisLength"
            :key="i"
          >
            <Icon class="size-10" :name="selectedPoi.category" />
            {{ t(`poi-details-card.${selectedPoi.category}.${selectedPoi.name}.${i}.title`) }}
          </PoiDetailsSubpoiButton>
        </div>
        <div class="flex flex-wrap justify-end gap-2">
          <FancyButton
            v-for="(key, index) in numberOfSelectedSubpoiLinks"
            :key="key"
            as="a"
            target="_blank"
            :href="
              t(
                `poi-details-card.${data.category}.${data.name}.${selectedSubpoiIndex}.links.${index}.url`,
              )
            "
            :color="`markets-${data.category}`"
            class="uppercase"
            >{{
              t(
                `poi-details-card.${data.category}.${data.name}.${selectedSubpoiIndex}.links.${index}.label`,
              )
            }}</FancyButton
          >
        </div>
      </div>
    </div>
    <div
      class="pointer-events-none sticky bottom-0 left-0 h-8 w-full bg-gradient-to-t from-white to-transparent"
    />
  </div>
</template>

<style scoped>
/* FIXME:  webkit-scrollbar doesn't apply styles on Firefox desktop*/

.custom-scrollbar::-webkit-scrollbar {
  @apply w-[18px] bg-transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply rounded-full border-[6px] border-solid border-transparent  shadow-[inset_0_0_6px_6px] shadow-filler-50 hover:cursor-pointer;
}
</style>
